import { useForm } from '@mantine/form';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import H2Medium from 'components/typography/H2Medium';
import P2Regular from 'components/typography/P2Regular';
import { noop } from 'lodash';
import { Checkbox, Divider, Group, Image, Stack, TextInput } from '@mantine/core';
import Button from 'components/Button';
import { FormMessage, FormProps } from 'components/forms/FormProps';
import useLoadingAction from 'hooks/use-loading-action';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import Toast from 'components/Toast';
import theme from 'theme';

export interface TwoFactorLoginFormData {
  code: string;
  isRecoveryCode: boolean;
}

export interface TwoFactorLoginFormProps extends FormProps<TwoFactorLoginFormData> {
  email: string;
  challengeExpiresIn?: number;
  onBack?: () => void;
  challengeMessage?: FormMessage;
}

/**
 * Form for completing login with a TOTP or recovery code.
 */
export default function TwoFactorLoginForm({
  email,
  challengeExpiresIn,
  initialValues = {
    code: '',
    isRecoveryCode: false,
  },
  onSubmit = noop,
  onBack = noop,
  challengeMessage,
}: TwoFactorLoginFormProps) {
  const [{ loading }, submit] = useLoadingAction(onSubmit);

  const form = useForm<TwoFactorLoginFormData>({
    initialValues,
    validate: {
      code: createValidator([required]),
    },
  });

  const expiresInMinutes = challengeExpiresIn ? Math.max(1, Math.ceil(challengeExpiresIn / 60)) : undefined;

  return (
    <form onSubmit={form.onSubmit(submit)}>
      <Stack p={24}>
        <Image src="/img/Objektologo.png" width={154} />
      </Stack>
      <Divider my={0} color="gray.1" />
      <Stack p={24} spacing={8}>
        <H2Medium>Dvojfaktorové overenie</H2Medium>
        <P2Regular color="gray.6">
          Prihlasujete sa ako {email}. Zadajte kód z autentifikačnej aplikácie alebo použite záložný kód.
        </P2Regular>
        {expiresInMinutes && (
          <P2Regular color="gray.6">Táto výzva vyprší približne o {expiresInMinutes} min.</P2Regular>
        )}
      </Stack>
      <Stack spacing={12} p={24} pt={12}>
        <TextInput
          size="lg"
          withAsterisk
          label={form.values.isRecoveryCode ? 'Záložný kód' : 'Overovací kód'}
          placeholder={form.values.isRecoveryCode ? 'ABCDE-FGHIJ' : '123456'}
          name="objekto-twoFactorCode"
          {...form.getInputProps('code')}
        />
        <Checkbox
          size="sm"
          label="Použiť záložný kód"
          color={theme.colors?.red?.[5]}
          name="objekto-isRecoveryCode"
          {...form.getInputProps('isRecoveryCode', { type: 'checkbox' })}
        />
        {challengeMessage && <Toast {...challengeMessage} opened fullWidth withCloseButton={false} />}
      </Stack>
      <Divider my={0} color="gray.1" />
      <Group p={24} grow>
        <Button
          type="button"
          size="lg"
          variant="tertiary"
          leftIcon={<IconChevronLeft stroke="1.5" width={24} />}
          onClick={onBack}
        >
          Späť
        </Button>
        <Button
          loading={loading}
          type="submit"
          size="lg"
          rightIcon={<IconChevronRight stroke="1.5" width={24} height={24} />}
        >
          Overiť kód
        </Button>
      </Group>
    </form>
  );
}
